import { IBooking, ICalendarNote, ILodgifyBooking } from "../../../types/models";
import { useTheme } from "../../../contexts/ThemeProvider";
import moment from "moment";
import {
  CheckOutlined,
  DollarOutlined,
  FileTextOutlined, HomeOutlined,
  LockOutlined, PlusSquareOutlined,
  SwapOutlined, TagsOutlined,
  UserOutlined
} from "@ant-design/icons";
import { Button, Divider, Dropdown, MenuProps, Popover, Space, Tooltip } from "antd";
import styled from "styled-components";
import { useState } from "react";
import dayjs from "dayjs";


const MonthContainer = styled.div<any>`
  padding: 10px;
  position: relative;
  color: ${({ isDark }) => isDark ? '#d6d6d6' : '#2a2b2c'};
  opacity: ${({ isPast }) => isPast ? 0.55 : 1};
`;

const MonthTitle = styled.h4`
  text-align: center;
  margin-top: 0;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
`;

const WeekContainer = styled.div<{ height: number }>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  height: ${({ height }) => height}px;
  position: relative;
`;

const DayContainer = styled.div`
  border: 0.5px solid rgba(126, 126, 126, 0.15);
  position: relative;
  cursor: pointer;

  &::before {
    position: absolute;
    top: -2px;
    left: 4px;
    content: "+";
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover {
    background: rgba(246, 202, 152, 0.33) !important;
  }
`;

const DayNumber = styled.span<any>`
  position: absolute;
  top: 2px;
  right: 6px;
  font-size: 12px;
  background: ${({ isToday }) => isToday ? '#c49662' : 'transparent'};
  color: ${({ isToday }) => isToday ? 'white' : 'inherit'};
  font-weight: ${({ isToday }) => isToday ? 'bold' : 'normal'};
  letter-spacing: -1px;
  padding-right: 1px;
  width: 20px;
  height: 20px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BookingBlock = styled.div<{ left: number; width: number; top: number; color?: string; textColor?: string }>`
  position: absolute;
  color: ${props => props.textColor || 'unset'};
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  left: ${props => props.left}%;
  width: ${props => props.width}%;
  top: ${props => props.top}px;
  z-index: 1;
  cursor: pointer;
  letter-spacing: -0.25px;
  box-shadow: 0 1px 2px rgba(0,0,0,.15);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const NoteBlock = styled.div<{ left: number; width: number; top: number; bgColor?: string; highlightColor?: string }>`
  position: absolute;
  background-color: ${props => props.bgColor };
  border-radius: 4px;
  padding: 3px 5px;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  left: ${props => props.left}%;
  width: ${props => props.width}%;
  top: ${props => props.top}px;
  z-index: 1;
  cursor: pointer;
  letter-spacing: -0.25px;
  // border: 1px solid lightgray;
  border-left: ${props => `3px ${props.highlightColor || '#9e9e9e'} solid`};
  box-shadow: 0 1px 2px rgba(0,0,0,.2);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ExternalBookingBlock = styled.div<{ left: number; width: number; top: number; color?: string; }>`
  position: absolute;
    // background-color: ${props => props.color || '#00a5a7'};
  // background: linear-gradient(90deg, rgba(2, 188, 156, 1) 0%, rgba(255, 59, 89, 1) 100%);
  background: linear-gradient(10deg, #af8a60 0%, #cda67b 100%);
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  left: ${props => props.left}%;
  width: ${props => props.width}%;
  top: ${props => props.top}px;
  z-index: 1;
  cursor: pointer;
  letter-spacing: -0.25px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .15);
  text-shadow: 0 0.5px 1px rgba(0,0,0, .75);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DayName = styled.div`
  font-size: 12px;
  text-align: center;
`;


const statusIcon: any = {
  "owner_stay": HomeOutlined,
  "held": LockOutlined,
  "available_for_rent": TagsOutlined,
  "rent_requested": TagsOutlined,
  "rented": CheckOutlined,
  "available_for_trade": SwapOutlined,
  "trade_requested": SwapOutlined,
  "traded": CheckOutlined,
  "exchange_requested": SwapOutlined,
  "exchanged": CheckOutlined,
  "maintenance": LockOutlined,
}

const statusLabel: any = {
  "owner_stay": "Owner stay",
  "held": "Held for sale",
  "available_for_rent": "Available for rent",
  "rent_requested": "Rent requested",
  "rented": "Rented out",
  "available_for_trade": "Available for trade",
  "trade_requested": "Trade requested",
  "traded": "Traded",
  "exchange_requested": "Exchange via Thirdhome requested",
  "exchanged": "Exchanged via Thirdhome",
  "maintenance": "Maintenance",
}

interface MonthCalendarProps {
  year: number;
  month: number;
  bookings: IBooking[];
  notes: ICalendarNote[];
  externalBookings: ILodgifyBooking[];
  setNoteToEdit: any;
  setBookingToEdit: any;
  ownerColors: Map<number, string>;
  ownerTextColors: Map<number, string>;
  setCreateBookingDate?: any;
  setCreateNoteDate?: any;
}

const MonthCalendar = ({ year, month, bookings, notes, externalBookings, setNoteToEdit, setBookingToEdit, ownerColors, ownerTextColors, setCreateBookingDate, setCreateNoteDate }: MonthCalendarProps) => {
  const { currentTheme } = useTheme();
  const monthName = moment.months()[month];
  const [dateToEdit, setDateToEdit] = useState<string>();

  const monthBookings = bookings.filter(booking => {
    const startDate = moment(booking.start_date);
    const endDate = moment(booking.end_date);
    return startDate.month() === month || endDate.month() === month;
  });

  const monthNotes = notes.filter(note => {
    const startDate = moment(note.start_date);
    const endDate = moment(note.end_date);
    return startDate.month() === month || endDate.month() === month;
  });

  const monthExternalBookings = externalBookings.filter(booking => {
    const startDate = moment(booking.arrival);
    const endDate = moment(booking.departure);
    return startDate.month() === month || endDate.month() === month;
  });

  const events = [
    ...monthBookings.map(b => ({ ...b, type: 'booking' })),
    ...monthNotes.map(n => ({ ...n, type: 'note' })),
    ...monthExternalBookings.map(b => ({ ...b, start_date: b.arrival, end_date: b.departure, type: 'external_booking' }))
  ];

  const renderWeeks = () => {
    const monthStart = moment({ year, month }).startOf('month');
    const monthEnd = moment({ year, month }).endOf('month');
    const startDate = monthStart.clone().startOf('week');
    const endDate = monthEnd.clone().endOf('week');

    const weeks = [];
    let currentDay = startDate.clone();

    const dayNames = moment.weekdaysShort().map(name => <DayName key={name}>{name}</DayName>);
    weeks.push(<WeekContainer key="day-names" height={18} style={{ opacity: 0.66 }}>{dayNames}</WeekContainer>);

    while (currentDay.isBefore(endDate)) {
      const weekStartMoment = currentDay.clone();
      const weekEndMoment = currentDay.clone().endOf('week');

      const weekEvents = events.filter(e => {
        const start = moment(e.start_date);
        const end = moment(e.end_date);
        return start.isBefore(weekEndMoment) && end.isAfter(weekStartMoment);
      });

      const lanes: any[][] = [];
      weekEvents.forEach((event: any) => {
        let placed = false;
        for (let i = 0; i < lanes.length; i++) {
          if (!lanes[i].some(placedEvent =>
            moment(event.start_date).isBefore(moment(placedEvent.end_date)) &&
            moment(event.end_date).isAfter(moment(placedEvent.start_date))
          )) {
            lanes[i].push(event);
            event.lane = i;
            placed = true;
            break;
          }
        }
        if (!placed) {
          event.lane = lanes.length;
          lanes.push([event]);
        }
      });

      const weekHeight = Math.max(lanes.length * 22 + 52, 45);

      const dayCells: any[] = [];
      const eventBlocks: any[] = [];

      const items: MenuProps['items'] = [
        {
          label: <a href="https://www.antgroup.com">1st menu item</a>,
          key: '0',
        },
        {
          label: <a href="https://www.aliyun.com">2nd menu item</a>,
          key: '1',
        },
        {
          type: 'divider',
        },
        {
          label: '3rd menu item',
          key: '3',
        },
      ];

      for (let i = 0; i < 7; i++) {
        const day = weekStartMoment.clone().add(i, 'days');
        if (day.month() === month) {
          dayCells.push(
            <Popover
              visible={day.format("YYYY-MM-DD") === dateToEdit}
              content={(
                <div className="disp-flex gap-xs">
                  <Button
                    size="small"
                    onClick={() => {
                      setBookingToEdit(null);
                      setCreateBookingDate(day.format("YYYY-MM-DD"));
                      setDateToEdit(undefined);
                    }}
                  >
                    Add Booking
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setCreateNoteDate(day.format("YYYY-MM-DD"));
                      setDateToEdit(undefined);
                    }}
                  >
                    Add Note
                  </Button>
                </div>
              )}
              onVisibleChange={(visible) => setDateToEdit(day.format(visible ? "YYYY-MM-DD" : undefined))}
              title={day.format("LL")}
              trigger="click"
            >
              <DayContainer
                key={i}
                style={{ background: currentTheme === 'dark' ? '#2a2a2a' : '#fff' }}
              >
                <DayNumber
                  isToday={moment().format('YYYY-MM-DD') === day.format('YYYY-MM-DD')}
                >
                  {day.date()}
                </DayNumber>
              </DayContainer>
            </Popover>)
        } else {
          dayCells.push(
            <DayContainer
              key={i}
              style={{ background: currentTheme === 'dark' ? '#242424' : '#f8f8f8' }}
            />
          );
        }
      }

      weekEvents.forEach((event: any) => {
        const start = moment(event.start_date);
        const end = moment(event.end_date);

        let effectiveStart = start.isBefore(weekStartMoment) ? weekStartMoment : start;
        let effectiveEnd = end.isAfter(weekEndMoment) ? weekEndMoment : end;

        const left = effectiveStart.day() * (100 / 7) + 1;
        const width = (effectiveEnd.day() - effectiveStart.day() + 1) * (100 / 7) - 2;
        const top = (event.lane || 0) * 22 + 24;

        if (event.type === 'booking') {
          const ownerId = (event as IBooking).unit_owner?.id;
          // @ts-ignore
          const isEmptyFraction = !event.owner_first_name;
          const color = ownerId ? ownerColors.get(ownerId) : undefined;
          const textColor = ownerId ? ownerTextColors.get(ownerId) : currentTheme === 'dark' ? '#b1b1b1' : '#404040';
          const blockTitle = isEmptyFraction ? `Empty fraction` : event.owner_first_name;
          const composedStatus = (isEmptyFraction && event.status === "owner_stay") ? "held" : event.status;
          const StatIcon = statusIcon[composedStatus] || HomeOutlined;
          const statLabel = statusLabel[composedStatus];
          const LeftIcon = isEmptyFraction ? PlusSquareOutlined : UserOutlined;

          eventBlocks.push(
            <BookingBlock
              style={{
                color: currentTheme === 'dark' ? 'white' : 'unset',
                backgroundColor: color || (currentTheme === 'dark' ? '#222222' : '#f5f5f5')
              }}
              key={`${event.type}-${event.id}`}
              left={left} width={width} top={top}
              onClick={() => setBookingToEdit(event)}
              color={color}
              textColor={textColor}
            >
              <LeftIcon style={{ fontSize: 14, color: textColor || 'black', opacity: 0.75 }} />
              <div style={{ lineHeight: 1.15, flex: 1 }}>
                <div style={{ fontWeight: 600, color: textColor || 'black' }}>{blockTitle}</div>
                <div style={{ fontSize: 12, color: textColor || 'black', opacity: 0.75, fontStyle: event.status === "owner_stay" ? "normal" : "italic" }}>{statLabel || "Owner Stay"}</div>
              </div>
              <div className={`disp-flex ${event.status === "owner_stay" ? '' : 'shine-pulse'}`} style={{ width: 27, height: 25, overflow: 'hidden', background: 'rgba(125,125,125,.12)', justifyContent: 'center', borderRadius: 4 }}>
                <StatIcon style={{ color: textColor, opacity: 0.75, fontSize: 14 }} />
              </div>
            </BookingBlock>);
        } else if (event.type === 'note') {
          const ownerId = (event as any).owner;
          const color = ownerId ? ownerColors.get(ownerId) : undefined;
          eventBlocks.push(
            <Tooltip title={event.title}>
              <NoteBlock
                style={{ color: currentTheme === 'dark' ? '#e3ddd3' : 'black' }}
                key={`${event.type}-${event.id}`}
                left={left} width={width} top={top+18}
                onClick={() => setNoteToEdit(event)} color={color}
                bgColor={currentTheme === 'dark' ? '#393833' : '#F1EFE8'}
              >
                <FileTextOutlined style={{ fontSize: 12, opacity: 0.66 }} /> {event.title}
              </NoteBlock></Tooltip>);
        } else if (event.type === 'external_booking') {
          const blockTitle = `For Rent: ${event.guest.name} (${event.source})`;
          eventBlocks.push(
            <Tooltip title={blockTitle}>
              <ExternalBookingBlock
                style={{
                  color: 'white',
                }}
                key={`${event.type}-${event.id}`}
                left={left} width={width} top={top}
              >
                <DollarOutlined style={{ fontSize: 11 }} /> {blockTitle}
              </ExternalBookingBlock>
            </Tooltip>
          );
        }
      });

      weeks.push(<WeekContainer key={currentDay.week()} height={weekHeight}>{dayCells}{eventBlocks}</WeekContainer>);
      currentDay.add(1, 'week');
    }

    return <div>{weeks}</div>;
  }

  return (
    <MonthContainer isDark={currentTheme === 'dark'} isPast={moment().isSameOrAfter(moment({ year, month: month + 1 }))}>
      <MonthTitle>{monthName}</MonthTitle>
      {renderWeeks()}
    </MonthContainer>
  );
};

export default MonthCalendar;
